import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { profileAPI, API_BASE_URL, IMG_URL} from '../utils/api';
import { Card, Form, Input, DatePicker, Select, Button, message, Space, Upload, Avatar } from 'antd';
import { UserOutlined, ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { validateNickname, validateTextField, validateWebsite, validateDescription } from '../utils/validator';
import useDocumentTitle from '../hooks/useDocumentTitle';

// 设置 dayjs 为 antd 的日期库
dayjs.locale('zh-cn');

const { Option } = Select;
const { TextArea } = Input;

const ProfileEdit = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  // 设置网页标题
  useDocumentTitle('编辑个人资料', {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileAPI.getProfile(username);
      setProfile(response.data);
      form.setFieldsValue({
        ...response.data,
        birthday: response.data.birthday ? dayjs(response.data.birthday) : null,
      });
    } catch (error) {
      console.error('获取用户资料失败:', error);
      message.error('获取用户资料失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (info) => {
    if (info.file.status === 'done') {
      message.success('头像上传成功');
      fetchProfile();
    } else if (info.file.status === 'error') {
      message.error('头像上传失败');
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件');
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB');
      return false;
    }
    return true;
  };

  const handleSubmit = async (values) => {
    try {
      // 前端验证
      if (values.nickname) {
        const nicknameResult = validateNickname(values.nickname);
        if (!nicknameResult.valid) {
          message.error(nicknameResult.message);
          return;
        }
      }

      if (values.name) {
        const nameResult = validateTextField(values.name, { fieldName: '姓名', max: 50 });
        if (!nameResult.valid) {
          message.error(nameResult.message);
          return;
        }
      }

      if (values.location) {
        const locationResult = validateTextField(values.location, { fieldName: '所在地', max: 100 });
        if (!locationResult.valid) {
          message.error(locationResult.message);
          return;
        }
      }

      if (values.bio) {
        const bioResult = validateDescription(values.bio, { fieldName: '个人简介', max: 500 });
        if (!bioResult.valid) {
          message.error(bioResult.message);
          return;
        }
      }

      if (values.website) {
        const websiteResult = validateWebsite(values.website);
        if (!websiteResult.valid) {
          message.error(websiteResult.message);
          return;
        }
      }

      if (values.interests) {
        const interestsResult = validateTextField(values.interests, { fieldName: '兴趣爱好', max: 200 });
        if (!interestsResult.valid) {
          message.error(interestsResult.message);
          return;
        }
      }

      if (values.signature) {
        const signatureResult = validateDescription(values.signature, { fieldName: '个性签名', max: 100 });
        if (!signatureResult.valid) {
          message.error(signatureResult.message);
          return;
        }
      }

      setSaving(true);
      const submitData = {
        ...values,
        birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : null,
      };
      await profileAPI.updateProfile(username, submitData);
      message.success('资料更新成功');
      navigate(`/${username}/iden/profile/`);
    } catch (error) {
      console.error('资料更新失败:', error);
      message.error('资料更新失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/${username}/iden/profile/`);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          返回
        </Button>
      </div>

      <Card title="编辑个人资料">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
            <div style={{ flex: 1 }}>
              <Form.Item label="头像">
                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                  <Avatar
                    size={128}
                    src={profile?.avatar ? `${IMG_URL}${profile.avatar}` : undefined}
                    icon={<UserOutlined />}
                  />
                  <Upload
                    name="file"
                    showUploadList={false}
                    action={`${API_BASE_URL}/iden/profiles/user/${username}/avatar`}
                    headers={{
                      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
                    }}
                    beforeUpload={beforeUpload}
                    onChange={handleAvatarChange}
                    accept="image/*"
                  >
                    <Button icon={<UploadOutlined />} size="small">
                      更换头像
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>
            </div>
          </div>

          <Form.Item
            label="昵称"
            name="nickname"
            rules={[{ required: true, message: '请输入昵称' }]}
          >
            <Input placeholder="请输入昵称" />
          </Form.Item>

          <Form.Item
            label="姓名"
            name="name"
          >
            <Input placeholder="请输入真实姓名" />
          </Form.Item>

          <Form.Item
            label="性别"
            name="gender"
          >
            <Select placeholder="请选择性别">
              <Option value="1">男</Option>
              <Option value="2">女</Option>
              <Option value="0">保密</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="生日"
            name="birthday"
          >
            <DatePicker style={{ width: '100%' }} placeholder="请选择生日" />
          </Form.Item>

          <Form.Item
            label="所在地"
            name="location"
          >
            <Input placeholder="请输入所在地" />
          </Form.Item>

          <Form.Item
            label="个人简介"
            name="bio"
          >
            <TextArea rows={4} placeholder="介绍一下自己吧" />
          </Form.Item>

          <Form.Item
            label="个人网站"
            name="website"
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            label="兴趣爱好"
            name="interests"
          >
            <Input placeholder="请输入兴趣爱好" />
          </Form.Item>

          <Form.Item
            label="个性签名"
            name="signature"
          >
            <TextArea rows={2} placeholder="写一句个性签名" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={handleBack}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ProfileEdit;
